// src/pages/task/TaskTypeListPage.tsx
// Task types — primary action top right, the list in a card. No page-level
// breadcrumb: the app top bar already shows Settings › Task types.
//
// Columns answer what a person scans this table for: which types exist, what
// they turn on, whether they are active, and how they move. Product was a
// single fixed UUID on every row — noise, and it is derived from the current
// scope anyway. Source entity was empty for every standalone type, so it moved
// under the name where it only appears when it is set.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useTaskTaskType, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';
import { ProjectIcon } from '@/components/IconPicker';

export const TaskTypeListPage = observer(() => {
  const store = useTaskTaskType();
  const { t } = useI18n();

  useEffect(() => {
    store.ListTaskTypes({ pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [] } as any);
  }, []);

  const handleDelete = async (typeId: string) => {
    if (!confirm(t('task.deleteTypeConfirm'))) return;
    try {
      await store.DeleteTaskType(typeId);
      store.ListTaskTypes({ pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [] } as any);
    } catch { /* store.error */ }
  };

  const isEmpty = !store.isLoading && store.items.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('task.taskTypes')}</h1>
        <Link to="/settings/task-types/new" className="shrink-0 sm:ml-auto">
          <Button>{t('task.addType')}</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        {isEmpty ? (
          /* Nothing to show means no table chrome — eight column titles above
             "no task types yet" is a table pretending to have data. */
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('task.noTypesYet')}</p>
            <Link to="/settings/task-types/new" className="inline-block">
              <Button size="sm">{t('task.addType')}</Button>
            </Link>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">{t('task.typeNameColumn')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('task.typeSlug')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('task.typeFeatures')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('task.lifecycle.title')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('common.status')}</TableHead>
                  <TableHead className="whitespace-nowrap text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {store.isLoading ? (
                  <>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><div className="h-4 w-40 animate-pulse rounded bg-muted" /></TableCell>
                        <TableCell><div className="h-4 w-28 animate-pulse rounded bg-muted" /></TableCell>
                        <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                        <TableCell><div className="h-4 w-20 animate-pulse rounded bg-muted" /></TableCell>
                        <TableCell><div className="h-5 w-14 animate-pulse rounded-full bg-muted" /></TableCell>
                        <TableCell><div className="ml-auto h-4 w-20 animate-pulse rounded bg-muted" /></TableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  store.items.map((item: any) => {
                    const itemId = item.entity?.id || item.id;
                    const isActive = item.is_active !== false && item.active !== false;
                    const transitionCount = Array.isArray(item.transitions) ? item.transitions.length : 0;
                    const statusCount = Array.isArray(item.status_definitions) ? item.status_definitions.length : 0;
                    const features: string[] = [];
                    if (item.enable_time_tracking) features.push(t('task.typeFeature.time'));
                    if (item.enable_checklists) features.push(t('task.typeFeature.checklists'));
                    if (item.enable_dependencies) features.push(t('task.typeFeature.dependencies'));
                    if (item.enable_watchers) features.push(t('task.typeFeature.watchers'));
                    return (
                      <TableRow key={itemId || Math.random()}>
                        <TableCell>
                          <div className="flex items-start gap-2.5">
                            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted">
                              <ProjectIcon
                                iconId={item.icon}
                                className="h-3.5 w-3.5"
                                style={item.color ? { color: item.color } : undefined}
                              />
                            </span>
                            <div className="min-w-0">
                              <div className="font-medium text-foreground">{String(item.display_name ?? '')}</div>
                              {item.source_entity_type ? (
                                <div className="text-xs text-muted-foreground">
                                  {t('task.typeSourceEntityRow', { value: String(item.source_entity_type) })}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                            {String(item.slug ?? '')}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {features.map((label) => (
                              <Badge key={label} variant="secondary" size="sm">{label}</Badge>
                            ))}
                            {item.requires_assignee ? (
                              <Badge variant="warning" size="sm">{t('task.typeFeature.requiresAssignee')}</Badge>
                            ) : null}
                            {features.length === 0 && !item.requires_assignee ? (
                              <span className="text-sm text-muted-foreground">—</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* A type with no transitions is not unconfigured — it runs
                              on the platform's default state machine. Say which. */}
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant={transitionCount > 0 ? 'primary' : 'secondary'} size="sm">
                              {transitionCount > 0
                                ? t('task.lifecycle.list.strict', { count: transitionCount })
                                : t('task.lifecycle.list.free')}
                            </Badge>
                            {statusCount > 0 && (
                              <Badge variant="secondary" size="sm">
                                {t('task.lifecycle.list.statuses', { count: statusCount })}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={isActive ? 'success' : 'secondary'} size="sm">
                            {isActive ? t('common.active') : t('common.inactive')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {/* The edit route is /settings/task-types/:id — the
                                old link added a /edit segment that matches no
                                route, so Edit fell through to the catch-all. */}
                            <Link to={`/settings/task-types/${itemId}`}>
                              <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(itemId)}
                            >
                              {t('common.delete')}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>
    </div>
  );
});

export default TaskTypeListPage;
